import { useState, type ReactNode } from 'react';
import {
  CALL_FEATURE_HINTS,
  CALL_FEATURE_IDS,
  CALL_FEATURE_LABELS,
  DEFAULT_CALL_FEATURES,
  ENVIRONMENT_IDS,
  RUN_STAGE_LABELS,
  needsPortalForCall,
  needsVoiceForCall,
  resolveCallFeatures,
  type CallFeatures,
  type EnvironmentId,
  type RunDetail,
  type RunRequest,
  type RunStage,
} from '@sfpbxtestbot/contracts';

type PackageAction = NonNullable<RunRequest['options']>['packageAction'];
import { ApiError, api } from '../lib/api.js';
import { useRemembered } from '../lib/useAsync.js';
import { Button, Callout, Card, Checkbox, Field } from './primitives.js';

export interface RunFormProps {
  stages: RunStage[];
  /** Fields that this screen actually needs; the rest stay hidden. */
  show: {
    packageUrl?: boolean;
    externalCredential?: boolean;
    call?: boolean;
  };
  submitLabel: string;
  onStarted: (run: RunDetail) => void;
}

/**
 * The run form.
 *
 * Values that rarely change between runs are remembered locally so the operator
 * is not retyping an org URL every time. The two secrets are never remembered:
 * they belong in the vault, and a client secret sitting in localStorage would be
 * exactly the kind of convenience nobody asked for.
 */
export function RunForm({ stages, show, submitLabel, onStarted }: RunFormProps): ReactNode {
  const [environment, setEnvironment] = useRemembered<EnvironmentId>('run.environment', 'qa');
  const [orgUrl, setOrgUrl] = useRemembered('run.orgUrl', '');
  const [tenantCode, setTenantCode] = useRemembered('run.tenantCode', '');
  const [packageUrl, setPackageUrl] = useRemembered('run.packageUrl', '');
  const [runAsUsername, setRunAsUsername] = useRemembered('run.runAs', '');
  const [userProfile, setUserProfile] = useRemembered('run.profile', 'System Administrator');
  const [agentNumber, setAgentNumber] = useRemembered('run.agentNumber', '');
  const [destinationNumber, setDestinationNumber] = useRemembered('run.destination', '');
  const [customerNumber, setCustomerNumber] = useRemembered('run.customerNumber', '');
  const [customerPhone, setCustomerPhone] = useRemembered<'pbx-extension' | 'google-voice'>(
    'run.customerPhone',
    'pbx-extension',
  );
  const [googleVoiceNumber, setGoogleVoiceNumber] = useRemembered('run.googleVoiceNumber', '');
  const [callSeconds, setCallSeconds] = useRemembered('run.callSeconds', 180);
  const [callDirection, setCallDirection] = useRemembered<'outbound' | 'inbound'>(
    'run.callDirection',
    'outbound',
  );
  const [callFeatures, setCallFeatures] = useRemembered<CallFeatures>(
    'run.callFeatures',
    DEFAULT_CALL_FEATURES,
  );
  const features = resolveCallFeatures(callFeatures);

  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const [packageAction, setPackageAction] = useRemembered<PackageAction>('run.packageAction', 'reinstall');
  const [uninstallFirst, setUninstallFirst] = useRemembered('run.uninstall', true);
  const [allowDestructive, setAllowDestructive] = useRemembered('run.destructive', true);
  const [allowFallback, setAllowFallback] = useRemembered('run.fallback', true);
  const [dryRun, setDryRun] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [problems, setProblems] = useState<{ field: string; message: string }[]>([]);

  async function submit(): Promise<void> {
    setSubmitting(true);
    setError(undefined);
    setProblems([]);

    const request: RunRequest = {
      environment,
      stages,
      salesforceOrgUrl: orgUrl.trim(),
      packageInstallUrl: packageUrl.trim(),
      clientId,
      clientSecret,
      tenantCode: tenantCode.trim(),
      agentNumber: agentNumber.trim(),
      destinationNumber: destinationNumber.trim(),
      customerNumber: customerNumber.trim(),
      googleVoiceNumber: googleVoiceNumber.trim(),
      runAsUsername: runAsUsername.trim(),
      userProfile,
      options: {
        packageAction,
        uninstallFirst: packageAction === 'upgrade' ? false : uninstallFirst,
        allowDestructiveCleanup: packageAction === 'upgrade' ? false : allowDestructive,
        allowFlexiPageFallback: allowFallback,
        dryRun,
        callDurationSeconds: callSeconds,
        callDirection,
        customerPhone,
        headed: false,
        callFeatures: features,
      },
    };

    try {
      onStarted(await api.startRun(request));
    } catch (cause) {
      if (cause instanceof ApiError) {
        setError(cause.message);
        setProblems(cause.problems);
      } else {
        setError(cause instanceof Error ? cause.message : 'The run could not be started.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card
      title="Start a run"
      subtitle={`This will run: ${stages.map((stage) => RUN_STAGE_LABELS[stage]).join(', ')}.`}
    >
      <div className="stack">
        <div className="grid grid--2">
          <Field label="Environment" hint="Sets the gateway, Remote Sites and Trusted URLs used throughout.">
            <select
              className="select"
              value={environment}
              onChange={(event) => setEnvironment(event.target.value as EnvironmentId)}
            >
              {ENVIRONMENT_IDS.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Salesforce org URL" hint="For example https://acme.my.salesforce.com">
            <input
              className="input"
              value={orgUrl}
              placeholder="https://acme.my.salesforce.com"
              onChange={(event) => setOrgUrl(event.target.value)}
            />
          </Field>

          <Field label="Tenant code" hint="Used to look up bookmarks and phone numbers.">
            <input
              className="input"
              value={tenantCode}
              placeholder="acme"
              onChange={(event) => setTenantCode(event.target.value)}
            />
          </Field>

          {show.packageUrl ? (
            <Field
              label="Package installation URL"
              hint="The RocketPhone.ai install link. It will be installed for all users."
            >
              <input
                className="input"
                value={packageUrl}
                placeholder="https://login.salesforce.com/packaging/installPackage.apexp?p0=04t..."
                onChange={(event) => setPackageUrl(event.target.value)}
              />
            </Field>
          ) : null}

          {show.packageUrl ? (
            <Field
              label="Package action"
              hint="Upgrade leaves the org's configuration alone. Uninstall then install starts from a clean package."
            >
              <select
                className="select"
                value={packageAction}
                onChange={(event) => setPackageAction(event.target.value as PackageAction)}
              >
                <option value="reinstall">Uninstall then install (then configure the org)</option>
                <option value="upgrade">Upgrade the existing package only</option>
              </select>
            </Field>
          ) : null}

          {show.externalCredential && packageAction !== 'upgrade' ? (
            <>
              <Field
                label="External Credential client ID"
                hint="Goes on the RocketPhonePrincipal. Stored encrypted, never shown again."
              >
                <input
                  className="input"
                  value={clientId}
                  autoComplete="off"
                  onChange={(event) => setClientId(event.target.value)}
                />
              </Field>
              <Field label="External Credential client secret" hint="Stored encrypted, never shown again.">
                <input
                  className="input"
                  type="password"
                  value={clientSecret}
                  autoComplete="new-password"
                  onChange={(event) => setClientSecret(event.target.value)}
                />
              </Field>
              <Field label="Run As username" hint="Used by the External Client App client credentials flow.">
                <input
                  className="input"
                  value={runAsUsername}
                  placeholder="you@example.com"
                  onChange={(event) => setRunAsUsername(event.target.value)}
                />
              </Field>
              <Field label="User profile" hint="Whose profile the record pages are activated for.">
                <input
                  className="input"
                  value={userProfile}
                  onChange={(event) => setUserProfile(event.target.value)}
                />
              </Field>
            </>
          ) : null}

          {show.call ? (
            <>
              <Field
                label="Call direction"
                hint={
                  callDirection === 'inbound'
                    ? 'The customer phone calls the agent number through the PBX, so the agent number’s inbound flow runs as for a real customer.'
                    : 'The agent softphone places the call, so RocketPhone logs, transcribes and writes a Task for it.'
                }
              >
                <select
                  className="select"
                  value={callDirection}
                  onChange={(event) => setCallDirection(event.target.value as 'outbound' | 'inbound')}
                >
                  <option value="outbound">Outbound — agent calls the customer</option>
                  <option value="inbound">Inbound — customer calls the agent</option>
                </select>
              </Field>
              <Field
                label="Agent number"
                hint={
                  callDirection === 'inbound'
                    ? 'The number the customer phone dials to reach this agent.'
                    : "Which of the agent's numbers to call from. Several may be visible but only some will work."
                }
              >
                <input
                  className="input"
                  value={agentNumber}
                  placeholder="+44 20 7946 0000"
                  onChange={(event) => setAgentNumber(event.target.value)}
                />
              </Field>
              <Field
                label="Customer phone"
                hint="What plays the customer on the call."
              >
                <select
                  className="select"
                  value={customerPhone}
                  onChange={(event) => setCustomerPhone(event.target.value as 'pbx-extension' | 'google-voice')}
                >
                  <option value="pbx-extension">Temporary RocketPhone phone (automatic)</option>
                  <option value="google-voice">Google Voice number (sign in once in Settings)</option>
                </select>
              </Field>
              {customerPhone === 'google-voice' ? (
                <Field
                  label="Google Voice number"
                  hint={
                    callDirection === 'inbound'
                      ? 'Google Voice calls the agent number from this number.'
                      : 'The agent calls this number and Google Voice answers.'
                  }
                >
                  <input
                    className="input"
                    value={googleVoiceNumber}
                    placeholder="+1 555 010 0000"
                    onChange={(event) => setGoogleVoiceNumber(event.target.value)}
                  />
                </Field>
              ) : (
                <Field
                  label="Customer number (optional)"
                  hint="Leave empty: the run borrows an unused number from your RocketPhone tenant for the pretend customer and hands it back afterwards. Fill in only to force a specific number."
                >
                  <input
                    className="input"
                    value={customerNumber}
                    placeholder="Picked automatically"
                    onChange={(event) => setCustomerNumber(event.target.value)}
                  />
                </Field>
              )}
              {callDirection === 'outbound' && customerPhone !== 'google-voice' ? (
                <Field
                  label="Number to dial"
                  hint="Optional backup. Used only if no customer number is available: the agent dials it and the customer lines play through the agent microphone."
                >
                  <input
                    className="input"
                    value={destinationNumber}
                    placeholder="+44 7700 900000"
                    onChange={(event) => setDestinationNumber(event.target.value)}
                  />
                </Field>
              ) : null}
              <Field label="Conversation length" hint="How long the bot talks before hanging up.">
                <select
                  className="select"
                  value={callSeconds}
                  onChange={(event) => setCallSeconds(Number(event.target.value))}
                >
                  <option value={120}>2 minutes</option>
                  <option value={180}>3 minutes</option>
                  <option value={300}>5 minutes</option>
                  <option value={480}>8 minutes</option>
                </select>
              </Field>
            </>
          ) : null}
        </div>

        {show.call ? (
          <div className="stack stack--tight">
            <div className="row row--between">
              <div>
                <strong>Features to test</strong>
                <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
                  The call itself is always placed. Turn features off if you only want to prove
                  the phone connects, or to skip the Admin Portal and v2 bookmark lookup.
                </div>
              </div>
              <div className="row">
                <Button size="small" onClick={() => setCallFeatures(DEFAULT_CALL_FEATURES)}>
                  Everything
                </Button>
                <Button
                  size="small"
                  onClick={() =>
                    setCallFeatures({
                      'live-transcript': false,
                      'live-bookmarks': false,
                      'call-assist': false,
                      'question-assist': false,
                      'rocket-form': false,
                      checklist: false,
                    })
                  }
                >
                  Call only
                </Button>
              </div>
            </div>
            <div className="grid grid--2">
              {CALL_FEATURE_IDS.map((id) => (
                <Checkbox
                  key={id}
                  label={CALL_FEATURE_LABELS[id]}
                  hint={CALL_FEATURE_HINTS[id]}
                  checked={features[id]}
                  onChange={(checked) => setCallFeatures({ ...features, [id]: checked })}
                />
              ))}
            </div>
            {!needsVoiceForCall(features) ? (
              <Callout tone="info">
                This run will only place the call and confirm it connects. No Admin Portal token or
                Google AI key is needed, and v2 bookmarks and checklists will not be read.
              </Callout>
            ) : !needsPortalForCall(features) ? (
              <Callout tone="info">
                Live Bookmarks and Call Assist are off, so the Admin Portal will not be used. V2
                checklist items are still read from the gateway if Checklist is on.
              </Callout>
            ) : null}
          </div>
        ) : null}

        <details className="disclosure">
          <summary>Advanced options</summary>
          <div className="stack stack--tight" style={{ marginTop: 12 }}>
            {show.packageUrl && packageAction !== 'upgrade' ? (
              <>
                <Checkbox
                  label="Uninstall any existing package version first"
                  hint="Every run starts from zero, which is the only way to prove the install itself works."
                  checked={uninstallFirst}
                  onChange={setUninstallFirst}
                />
                <Checkbox
                  label="Clear RocketPhone call records if they block the uninstall"
                  hint="Salesforce refuses to uninstall while activity records reference the package. This removes only that call data."
                  checked={allowDestructive}
                  onChange={setAllowDestructive}
                />
                <Checkbox
                  label="Place Lightning components via the Metadata API"
                  hint="This is the reliable path. Turn it off only if you want the run to try Lightning App Builder drag-and-drop instead."
                  checked={allowFallback}
                  onChange={setAllowFallback}
                />
              </>
            ) : null}
            <Checkbox
              label="Dry run"
              hint="Plan the run and validate everything without touching any live system."
              checked={dryRun}
              onChange={setDryRun}
            />
          </div>
        </details>

        {error ? (
          <Callout tone="danger">
            <div className="stack stack--tight">
              <strong>{error}</strong>
              {problems.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {problems.map((problem) => (
                    <li key={`${problem.field}:${problem.message}`}>{problem.message}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </Callout>
        ) : null}

        <div className="row">
          <Button variant="primary" busy={submitting} onClick={() => void submit()}>
            {packageAction === 'upgrade' && show.packageUrl ? 'Upgrade the package' : submitLabel}
          </Button>
          {dryRun ? <span className="muted">Nothing will be changed in Salesforce.</span> : null}
        </div>
      </div>
    </Card>
  );
}
